import {
  Box,
  Button,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RenderRadioGroup from "components/RenderRadioGroup";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AddEditGoalsAssessmentPayload,
  AddPersonalAndSmartGoalsPayload,
  AddPersonalAndSmartGoalsSuccessPayload,
  DeletePersonalAndSmartGoalsPayload,
  GetCurrentSLPGoalsPDFPayload,
  PersonalAndSmartGoalsBySlpIdPayload,
  PersonalAndSmartGoalsPayload,
} from "store/slpForm/types";
import { INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX } from "utils/constants/constant";
import { PERSONAL_SMART_GOALS_ADDED_SUCCESS_MSG } from "utils/constants/Messages";
import {
  PERSONAL_SMART_GOALS_MAX_LENGTH,
  goalsTextFieldConfigs,
} from "utils/constants/slp";
import {
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  getDifficultyError,
  getImportanceError,
} from "utils/helper/validation-message-helper";
import { SLPFormStep } from "utils/enums/slp-form";
import { IAddPersonalSmartGoals } from "utils/interfaces/slp-form";
import SLPGoalsTable from "./SLPGoalsTable";
import SLPFormButtons from "../SLPFormButtons";

interface IPersonalAndSmartGoalsProps {
  personalAndSmartGoalsRequest: (payload: PersonalAndSmartGoalsPayload) => void;
  personalAndSmartGoalsBySlpIdRequest: (
    payload: PersonalAndSmartGoalsBySlpIdPayload
  ) => void;
  addPersonalAndSmartGoalsRequest: (
    payload: AddPersonalAndSmartGoalsPayload
  ) => {};
  addEditGoalsAssessmentRequest: (payload: AddEditGoalsAssessmentPayload) => {};
  deletePersonalAndSmartGoalsRequest: (
    payload: DeletePersonalAndSmartGoalsPayload
  ) => {};
  getCurrentSLPGoalsPDFRequest: (payload: GetCurrentSLPGoalsPDFPayload) => {};
  handleNext: () => void;
}

const PersonalAndSmartGoals: React.FC<IPersonalAndSmartGoalsProps> = (
  props
) => {
  const {
    personalAndSmartGoalsRequest,
    personalAndSmartGoalsBySlpIdRequest,
    addPersonalAndSmartGoalsRequest,
    addEditGoalsAssessmentRequest,
    deletePersonalAndSmartGoalsRequest,
    getCurrentSLPGoalsPDFRequest,
    handleNext,
  } = props;

  const location = useLocation<{
    slpId: number;
    studentId: number;
    partNumber: SLPFormStep;
  }>();
  const [getPersonalAndSmartGoals, setGetPersonalAndSmartGoals] =
    useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IAddPersonalSmartGoals>({
    defaultValues: {
      studentGoals: "",
      smartGoals: "",
      baselineLevel: "",
      importance: 0,
      difficulty: 0,
    },
  });

  const onDataChange = () => {
    setGetPersonalAndSmartGoals((prev) => !prev);
  };

  const onPersonalAndSmartGoalsSuccess = (
    response: AddPersonalAndSmartGoalsSuccessPayload
  ) => {
    if (response.success) {
      reset();
      toast.success(PERSONAL_SMART_GOALS_ADDED_SUCCESS_MSG);
      onDataChange();
      hideLoaderForBatchApiCall();
    }
  };

  const onSubmit = (data: IAddPersonalSmartGoals) => {
    if (addPersonalAndSmartGoalsRequest) {
      showLoaderForBatchApiCall();
      data.slpId = location.state.slpId;
      const payload = {
        values: {
          ...data,
          studentGoals: data.studentGoals.trim(),
          smartGoals: data.smartGoals.trim(),
          baselineLevel: data.baselineLevel.trim(),
        },
        callback: onPersonalAndSmartGoalsSuccess,
      };
      addPersonalAndSmartGoalsRequest(payload);
    }
  };

  return (
    <>
      <SLPGoalsTable
        requestHandler={personalAndSmartGoalsRequest}
        showAssessedColumn
        slpId={location.state.slpId}
        studentId={location.state.studentId}
        onDataModified={onDataChange}
        fetchData={getPersonalAndSmartGoals}
        addEditGoalsAssessmentRequest={addEditGoalsAssessmentRequest}
        deletePersonalAndSmartGoalsRequest={deletePersonalAndSmartGoalsRequest}
      />
      {/* <Typography variant="h6" sx={{ pt: 1.5, color: "#E54B4B" }}>
        Note: If this is a &apos;Final SLP&apos;, Skip part 2, Below - Do not
        add new goals
      </Typography> */}
      <Grid
        container
        sx={{ pt: 4 }}
        columnSpacing={{ xs: 2, lg: 3, xl: "24px" }}
      >
        <Grid item xs={12} lg={6}>
          <Typography variant="h5" className="medium-text" sx={{ pb: 1.5 }}>
            Add Goals
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              {goalsTextFieldConfigs(errors).map(
                ({ name, label, error, helper }) => (
                  <Grid item xs={12} key={name}>
                    <TextField
                      multiline
                      fullWidth
                      label={
                        <>
                          {label} <span className="color-red">*</span>
                        </>
                      }
                      variant="outlined"
                      className="suggestion-textarea"
                      rows={3}
                      error={!!error}
                      helperText={helper}
                      InputLabelProps={{
                        shrink: Boolean(
                          watch(name as keyof IAddPersonalSmartGoals)
                        ),
                      }}
                      {...register(name as keyof IAddPersonalSmartGoals, {
                        required: true,
                        maxLength: PERSONAL_SMART_GOALS_MAX_LENGTH,
                        pattern:
                          INPUT_VALIDATION_NO_WHITE_SPACE_WITH_SPECIAL_CHARACTER_REGEX,
                      })}
                    />
                  </Grid>
                )
              )}
              <Grid item xs={12} sm={6}>
                <RenderRadioGroup
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  name="importance"
                  label="Importance"
                  errorMsg={getImportanceError(errors.importance)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RenderRadioGroup
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  name="difficulty"
                  label="Difficulty"
                  errorMsg={getDifficultyError(errors.difficulty)}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" type="submit" sx={{ mt: 3 }}>
                  Add Goal
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Typography
            variant="body2"
            className="gray-text-sm"
            sx={{
              pb: 1.25,
              pt: {
                xs: 2,
                lg: 0,
              },
            }}
          >
            <strong style={{ color: "#424242", fontWeight: 500 }}>Note:</strong>{" "}
            Do not include a goal that is “not at all” important or one that is
            “not at all” difficult
          </Typography>

          <TableContainer>
            <Table className="cus-table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      width: "10px",
                      minWidth: "100px",
                      maxWidth: "100px",
                    }}
                  >
                    Importance
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: "10px",
                      minWidth: "100px",
                      maxWidth: "100px",
                    }}
                  >
                    Difficulty
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    Weight of goal = importance x Difficulty
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">0 = not at all (important)</TableCell>
                  <TableCell align="left">0 = not at all (difficult)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">1 = a little (important)</TableCell>
                  <TableCell align="left">1 = a little (difficult)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">2 = moderately (important)</TableCell>
                  <TableCell align="left">2 = moderately (difficult)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">3 = very (important)</TableCell>
                  <TableCell align="left">3 = very (difficult)</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ pt: { xs: 2, lg: 4 } }}>
            <Card className="p-0">
              <SLPGoalsTable
                requestHandler={personalAndSmartGoalsBySlpIdRequest}
                slpId={location.state.slpId}
                onDataModified={onDataChange}
                fetchData={getPersonalAndSmartGoals}
                deletePersonalAndSmartGoalsRequest={
                  deletePersonalAndSmartGoalsRequest
                }
                getCurrentSLPGoalsPDFRequest={getCurrentSLPGoalsPDFRequest}
              />
            </Card>
          </Box>
        </Grid>
      </Grid>
      <SLPFormButtons
        activePartNumber={location.state.partNumber}
        handleNext={handleNext}
      />
    </>
  );
};

export default PersonalAndSmartGoals;
